import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StaffMealsService } from '../../../../core/services/staff-meals.service';
import { Booking } from '../../../../app';

@Component({
  selector: 'app-rate-vendor-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rate-vendor-modal.html',
  styleUrl: './rate-vendor-modal.scss'
})
export class RateVendorModalComponent implements OnInit {
  @Input() booking!: Booking;
  @Input() isReadOnly: boolean = false;
  @Output() closed = new EventEmitter<void>();
  @Output() rated = new EventEmitter<{ bookingId: string; rating: number; note?: string }>();

  rating: number = 0;
  hoveredStar: number = 0;
  note: string = '';
  isSubmitting: boolean = false;
  isAlreadyRated: boolean = false;
  errorMessage: string = '';
  maxNoteLength: number = 250;

  constructor(
    private staffMealsService: StaffMealsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.booking?.hasVendorRating || this.isReadOnly) {
      this.fetchExistingRating();
    }
  }

  get vendorInitials(): string {
    const name = this.booking?.vendor || 'Vendor';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  fetchExistingRating(): void {
    if (!this.booking?.id) return;
    this.isSubmitting = true;
    this.staffMealsService.getVendorRating(this.booking.id).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        const data = res.data || res;
        if (data) {
          this.rating = data.rating || 0;
          this.note = data.note || '';
          this.isAlreadyRated = true;
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isSubmitting = false;
        console.error('Failed to load vendor rating:', err);
        this.cdr.detectChanges();
      }
    });
  }

  setRating(star: number): void {
    if (this.isAlreadyRated || this.isReadOnly) return;
    this.rating = star;
  }

  setHoveredStar(star: number): void {
    if (this.isAlreadyRated || this.isReadOnly) return;
    this.hoveredStar = star;
  }

  clearHoveredStar(): void {
    this.hoveredStar = 0;
  }

  submitRating(): void {
    if (this.rating < 1 || this.rating > 5 || this.isSubmitting || this.isAlreadyRated) return;

    this.isSubmitting = true;
    this.errorMessage = '';

    const payload = {
      bookingId: this.booking.id,
      rating: this.rating,
      note: this.note.trim() ? this.note.trim() : undefined
    };

    this.staffMealsService.rateVendor(payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        this.rated.emit({
          bookingId: this.booking.id,
          rating: this.rating,
          note: this.note
        });
        this.cdr.detectChanges();
        this.closed.emit();
      },
      error: (err: any) => {
        this.isSubmitting = false;
        if (err.status === 409) {
          this.isAlreadyRated = true;
          const existingData = err.error?.data;
          if (existingData) {
            this.rating = existingData.rating || this.rating;
            this.note = existingData.note || this.note;
          }
          this.errorMessage = 'You have already rated service for this transaction.';
        } else {
          this.errorMessage = err.error?.message || 'Failed to submit vendor rating. Please try again.';
        }
        this.cdr.detectChanges();
      }
    });
  }

  closeModal(): void {
    this.closed.emit();
  }
}
